import { Subscription, Enrollment } from '../types';
import { parseLocalDate } from './dateUtils';

export type RenewalStatus =
  | 'healthy'
  | '90-day'
  | '60-day'
  | '30-day'
  | 'expired'
  | 'n/a';

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function daysUntilExpiration(sub: Subscription, today: Date = new Date()): number {
  const expiry = startOfDay(parseLocalDate(sub.expirationDate));
  const now = startOfDay(today);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((expiry.getTime() - now.getTime()) / msPerDay);
}

export function getRenewalStatus(sub: Subscription, today: Date = new Date()): RenewalStatus {
  if (sub.status !== 'active') return 'n/a';
  const days = daysUntilExpiration(sub, today);
  if (days < 0) return 'expired';
  if (days <= 30) return '30-day';
  if (days <= 60) return '60-day';
  if (days <= 90) return '90-day';
  return 'healthy';
}

export function getRenewalLabel(status: RenewalStatus): string {
  switch (status) {
    case 'healthy':
      return 'Healthy';
    case '90-day':
      return 'Expires within 90 days';
    case '60-day':
      return 'Expires within 60 days';
    case '30-day':
      return 'Expires within 30 days';
    case 'expired':
      return 'Expired';
    case 'n/a':
      return '—';
  }
}

export function isExpiringSoon(status: RenewalStatus): boolean {
  return status === '30-day' || status === '60-day' || status === '90-day';
}

export function compareByExpiration(a: Subscription, b: Subscription): number {
  return parseLocalDate(a.expirationDate).getTime() - parseLocalDate(b.expirationDate).getTime();
}

export interface Utilization {
  used: number;
  total: number | null;
  label: string;
}

export function getSubscriptionUtilization(sub: Subscription, enrollments: Enrollment[]): Utilization {
  if (sub.tier === 'Starter') {
    const remaining = sub.coursesRemaining ?? 0;
    const used = Math.max(0, 1 - remaining);
    return { used, total: 1, label: `${used} / 1 courses used` };
  }

  if (sub.tier === 'Team') {
    const accepted = (sub.teamMembers ?? []).filter((m) => m.inviteStatus === 'accepted').length;
    const total = sub.studentList.length;
    return { used: accepted, total, label: `${accepted} / ${total} seats filled` };
  }

  // Pro: unlimited courses. Count enrollments linked to this subscription.
  const used = enrollments.filter(
    (e) => e.subscriptionId === sub.id && e.status !== 'cancelled'
  ).length;
  return { used, total: null, label: `${used} enrollment${used === 1 ? '' : 's'}` };
}
